<template>
    <div class="layout" :style="{ '--sidebar-width': `${sidebarWidth}px` }">
        <div class="main-container">
            <div class="side-menu-container" :class="{ collapsed: isSidebarCollapsed }">
                <div class="header-section">
                    <div class="collapse-btn" @click="toggleSidebar">
                        <el-icon>
                            <Expand v-if="isSidebarCollapsed" />
                            <Fold v-else />
                        </el-icon>
                    </div>
                    <div v-show="showTitle && !isSidebarCollapsed" class="title-text">建研国检智能检测系统</div>
                </div>
                <!-- 直接渲染菜单 -->
                <el-menu
                  :default-active="activeMenu"
                  :active="activeMenu"
                  :collapse="isSidebarCollapsed"
                  class="side-menu"
                  text-color="#bfcbd9"
                  active-text-color="#ffffff"
                  background-color="#001529"
                  :collapse-transition="false"
                  @select="handleSelect"
                >
                  <sidebar-menu-item
                    v-for="item in sidebarMenus"
                    :key="item.id || item.path"
                    :item="item"
                    :is-collapse="isSidebarCollapsed"
                  />
                </el-menu>
            </div>

            <div class="content-container" :class="{ expanded: isSidebarCollapsed }">
                <div class="header">
                    <div class="breadcrumb">
                        <span>{{ BreadcrumbTitle }} </span>
                        <span v-if="Breadcrumb"> / </span>
                        <span>{{ Breadcrumb }}</span>
                    </div>
                    <div class="user-section">
                        <div class="user-info" v-if="currentUserName">
                            <el-icon><User /></el-icon>
                            <span :title="currentUserName">{{ currentUserName }}</span>
                        </div>
                        <el-icon class="logout-icon" title="退出登录">
                            <SwitchButton @click="logout" />
                        </el-icon>
                    </div>
                </div>
                <div class="tabs-container">
                    <div class="tabs-wrapper">
                        <el-tabs v-model="activeTab" type="card" closable @tab-remove="removeTab" @tab-click="handleClick">
                            <el-tab-pane v-for="tab in tabs" :key="tab.path" :label="tab.title" :name="tab.path"
                                :closable="tab.path !== '/home'" @close="removeTab(tab.path)">
                            </el-tab-pane>
                        </el-tabs>
                        <div class="tabs-actions">
                            <el-button size="small" @click="closeOtherTabs" :disabled="!canCloseOtherTabs">关闭其他</el-button>
                            <el-button size="small" @click="closeAllTabs" :disabled="tabs.length <= 1">全部关闭</el-button>
                        </div>
                    </div>
                </div>
                <!-- 包装 router-view 使其具有独立滚动条 -->
                <div class="router-view-container">
                    <router-view v-slot="{ Component, route }">
                        <keep-alive :exclude="noCacheRoutes" :max="tabs.length + 1">
                            <component :is="Component" :key="getCacheKey(route.fullPath)"></component>
                        </keep-alive>
                    </router-view>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { SwitchButton, Expand, Fold, Menu, Document, Setting, User, House, Star } from '@element-plus/icons-vue';
import * as AllIcons from '@element-plus/icons-vue';
import { ref, watch, onMounted, computed } from 'vue';
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from 'element-plus';
import { useMenuStore } from '@/store/menuStore'; // 导入Pinia store
import { useDetailStore } from '@/store/detailStore';
import { usePageStateStore } from '@/store/pageStateStore';
import { home } from '@/network/index'; // 导入接口
import addDynamicRoutes from '@/utils/addRoutes'; // 导入动态路由
import { clearAuthCache } from '@/utils/authCache';
import SidebarMenuItem from './SidebarMenuItem.vue';

const router = useRouter();
const route = useRoute();
const menuStore = useMenuStore(); // 使用Pinia store
const detailStore = useDetailStore();
const pageStateStore = usePageStateStore();

// 菜单选择处理（正常跳转）
const handleSelect = (key: string) => {
  router.push(key);
}

// 图标映射函数
const getIconComponent = (iconName: string) => {
  if (!iconName) return Menu
  // 如果已经是组件，直接返回
  if (typeof iconName === 'object') {
    return iconName
  }
  
  // fa-* 旧格式映射
  const faIconMap: Record<string, any> = {
    'fa-star': Star,
    'fa-home': House,
    'fa-user': User,
    'fa-cog': Setting,
    'fa-file': Document,
    'fa-menu': Menu,
    'fa-sitemap': Menu,
  }
  
  if (iconName in faIconMap) {
    return faIconMap[iconName]
  }
  
  // 尝试按名称匹配 Element Plus 图标（全局已注册）
  const icons = AllIcons as Record<string, any>
  if (icons[iconName]) {
    return icons[iconName]
  }
  
  // 默认返回Menu图标
  return Menu
}

// 使用Pinia中的侧边栏菜单数据
const sidebarMenus = computed(() => menuStore.sidebarMenus);

const getTextDisplayWidth = (text: unknown) => {
    return Array.from(String(text || '')).reduce((width, character) => {
        return width + (/[^\x00-\xff]/.test(character) ? 16 : 8.5);
    }, 0);
};

const getRequiredMenuWidth = (menus: any[], depth = 0): number => {
    return menus.reduce((maxWidth, item) => {
        if (item?.IsHide || item?.IsButton) return maxWidth;

        const title = item?.meta?.title || item?.name || '';
        const hasChildren = Array.isArray(item?.children) && item.children.length > 0;
        const itemWidth = getTextDisplayWidth(title) + 104 + depth * 16 + (hasChildren ? 16 : 0);
        const childWidth = hasChildren ? getRequiredMenuWidth(item.children, depth + 1) : 0;
        return Math.max(maxWidth, itemWidth, childWidth);
    }, 0);
};

const sidebarWidth = computed(() => {
    const requiredWidth = getRequiredMenuWidth(sidebarMenus.value || []);
    return Math.ceil(Math.min(420, Math.max(220, requiredWidth)));
});

const BreadcrumbTitle = ref('');// 面包屑标题
const Breadcrumb = ref('');// 面包屑二级标题
const currentUserName = ref('');
const activeTab = ref('/home');
const tabs = ref<Array<{ title: string; path: string }>>([
    { title: '首页', path: '/home' }
]);

const canCloseOtherTabs = computed(() => {
    return tabs.value.some(tab => tab.path !== '/home' && tab.path !== activeTab.value);
});

// 缓存版本号（用于强制刷新特定路由的缓存）
const cacheKeys = ref<Record<string, number>>({});

// 获取路由的缓存 key（使用 fullPath 确保不同 query 参数的页面有独立缓存）
const getCacheKey = (fullPath: string) => {
    return `${fullPath}-${cacheKeys.value[fullPath] || 0}`;
};

// 不缓存的路由组件名称列表
const noCacheRoutes = ref<string[]>(['printList']);

const resetLayoutState = () => {
    activeTab.value = '/home';
    tabs.value = [{ title: '首页', path: '/home' }];
    cacheKeys.value = {};
    BreadcrumbTitle.value = '';
    Breadcrumb.value = '';
    currentUserName.value = '';
};


// 侧边栏收缩状态
const isSidebarCollapsed = ref(false);
// 标题显示状态（延迟显示，避免展开过程中换行）
const showTitle = ref(true);

// 添加当前激活菜单
const activeMenu = computed(() => route.path);

// 切换侧边栏收缩状态
const toggleSidebar = () => {
    if (isSidebarCollapsed.value) {
        // 展开：先展开侧边栏，延迟显示标题
        isSidebarCollapsed.value = false;
        showTitle.value = false;
        setTimeout(() => {
            showTitle.value = true;
        }, 300); // 等待展开动画完成
    } else {
        // 收缩：先隐藏标题，再收缩侧边栏
        showTitle.value = false;
        isSidebarCollapsed.value = true;
    }
};

interface MenuItem {
    id: string;
    pid?: string;
    path: string;
    name?: string;
    IsHide?: boolean;
    meta?: {
        title: string;
        icon?: string;
        [key: string]: any;
    };
    children?: MenuItem[];
}

interface TokenInfoResponse {
  response?: {
    uID?: string;
    uRealName?: string;
    uLoginName?: string;
    UserName?: string;
    userName?: string;
    Name?: string;
    name?: string;
    用户名?: string;
    姓名?: string;
  };
}

interface PermissionResponse {
  response?: MenuItem[] | { children?: MenuItem[] };
}

const extractMenusFromPermissionResponse = (res: PermissionResponse): MenuItem[] => {
  const responseData = res?.response
  if (!responseData) return []
  if (Array.isArray(responseData)) return responseData
  if (Array.isArray(responseData.children)) return responseData.children
  return []
}

const resolveUserName = (userInfo: any) => {
  return String(
    userInfo?.uRealName ||
    userInfo?.姓名 ||
    userInfo?.UserName ||
    userInfo?.userName ||
    userInfo?.用户名 ||
    userInfo?.uLoginName ||
    userInfo?.Name ||
    userInfo?.name ||
    ''
  ).trim();
};

// 加载菜单数据（每次进入都重新调用接口）
const loadMenuData = async () => {
  try {
    const token = sessionStorage.getItem('wuyetoken') || localStorage.getItem('wuyetoken');
    if (!token) {
      router.replace({ name: 'login' });
      return;
    }
    
    // 获取用户信息
    const tokenData = await home.GetInfoByToken({ token }) as TokenInfoResponse;
    currentUserName.value = resolveUserName(tokenData?.response);
    const uid = tokenData?.response?.uID;
    if (!uid) {
      console.error('获取用户信息失败');
      menuStore.loadMenusFromStorage(); // 降级使用缓存
      return;
    }
    
    // 获取用户权限菜单
    const permissionRes = await home.GetUserPermission(
      { uid },
      { suppressBusinessErrorMessage: true }
    ) as PermissionResponse;
    const menusData = extractMenusFromPermissionResponse(permissionRes);
    if (menusData.length > 0) {
      
      // 更新本地存储
      localStorage.setItem('wuyemenusJSON', JSON.stringify(menusData));
      
      // 更新菜单store
      menuStore.setMenus(menusData);
      
      // 添加动态路由
      addDynamicRoutes(router, menusData);
    } else {
      console.warn('当前用户无菜单权限');
      const cachedMenus = menuStore.loadMenusFromStorage();
      const syncedAt = Number(localStorage.getItem('menuPermissionSyncedAt') || 0);
      const justSynced = syncedAt > 0 && Date.now() - syncedAt < 10000;
      if (justSynced && cachedMenus.length > 0) {
        menuStore.setMenus(cachedMenus);
        addDynamicRoutes(router, cachedMenus);
        return;
      }
      localStorage.setItem('wuyemenusJSON', JSON.stringify([]));
      localStorage.setItem('buttonPermissions', JSON.stringify([]));
      menuStore.clearMenus();
    }
  } catch (error) {
    console.error('加载菜单数据失败:', error);
    // 出错时降级使用缓存
    menuStore.loadMenusFromStorage();
  }
};

onMounted(() => {
  // 每次进入都重新调用接口获取菜单数据
  loadMenuData();
});

const logout = () => {
    ElMessageBox.confirm('您确定要退出登录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
    }).then(() => {
        clearAuthCache();
        resetLayoutState();
        menuStore.clearMenus(); // 退出时清空菜单数据
        detailStore.clearAll();
        pageStateStore.clearSelectedId();
        router.replace({ name: "login" });
        ElMessage({
            type: 'success', message: '已退出',
        })
    }).catch(() => {
        ElMessage({ type: 'info', message: '已取消' })
    })
}

// 添加标签页
const addTab = (route: any) => {
    const { path, fullPath } = route;
    
    // 特殊处理首页
    if (path === '/home') {
        activeTab.value = fullPath;
        return;
    }

    // 使用 fullPath（包含 query 参数）作为唯一标识
    const isExist = tabs.value.some(tab => tab.path === fullPath);
    if (!isExist) {
        // 从处理后的侧边栏菜单中查找对应的标题
        let tabTitle = '未命名';
        
        // 递归查找菜单标题和IsHide状态
        const findTitleInMenus = (menus: MenuItem[], targetPath: string): { title: string | null, isHide: boolean } => {
            for (const menu of menus) {
                if (menu.path === targetPath) {
                    return {
                        title: menu.meta?.title || menu.name || '未命名',
                        isHide: menu.IsHide || false
                    };
                }
                if (menu.children && menu.children.length > 0) {
                    const found = findTitleInMenus(menu.children, targetPath);
                    if (found.title) return found;
                }
            }
            return { title: null, isHide: false };
        };

        const menuInfo = findTitleInMenus(menuStore.sidebarMenus, path);
        
        if (menuInfo.title) {
            tabTitle = menuInfo.title;
            
            // 如果是隐藏菜单项，不添加到标签页
            if (menuInfo.isHide) {
                return;
            }
        } else {
            // 如果在菜单中找不到，尝试从路由 meta 中获取标题
            if (route.meta?.title) {
                tabTitle = route.meta.title;
            } else {
                // 如果路由 meta 中也没有标题，不添加到标签页
                return;
            }
        }

        tabs.value.push({
            title: tabTitle,
            path: fullPath
        });
    }
    activeTab.value = fullPath;
};

// 移除标签页
const removeTab = (targetPath: string) => {
    // 防止删除首页标签
    if (targetPath === '/home') {
        ElMessage({
            type: 'warning',
            message: '首页标签不可删除'
        });
        return;
    }

    const targetIndex = tabs.value.findIndex(tab => tab.path === targetPath);
    if (targetIndex === -1) return;

    const isActive = activeTab.value === targetPath;

    // 非当前激活标签：立即递增缓存版本号（组件未渲染，不会触发重建）
    if (!isActive) {
        cacheKeys.value[targetPath] = (cacheKeys.value[targetPath] || 0) + 1;
    }

    tabs.value.splice(targetIndex, 1);
    if (isActive) {
        const nextTab = tabs.value[targetIndex] || tabs.value[targetIndex - 1];
        if (nextTab) {
            activeTab.value = nextTab.path;
            // 导航完成后再递增版本号，避免 key 变化在当前路由时触发组件重建导致额外接口调用
            router.push(nextTab.path).then(() => {
                cacheKeys.value[targetPath] = (cacheKeys.value[targetPath] || 0) + 1;
            });
        }
    }
};

const invalidateTabCaches = (paths: string[]) => {
    paths.forEach((path) => {
        if (path !== '/home') {
            cacheKeys.value[path] = (cacheKeys.value[path] || 0) + 1;
        }
    });
};

const closeAllTabs = () => {
    const closedPaths = tabs.value.map(tab => tab.path);
    invalidateTabCaches(closedPaths);
    tabs.value = [{ title: '首页', path: '/home' }];
    activeTab.value = '/home';
    if (route.path !== '/home') {
        router.push('/home');
    }
};

const closeOtherTabs = () => {
    const currentTab = tabs.value.find(tab => tab.path === activeTab.value);
    const homeTab = tabs.value.find(tab => tab.path === '/home') || { title: '首页', path: '/home' };
    const keepPaths = new Set(['/home', currentTab?.path].filter(Boolean) as string[]);
    const closedPaths = tabs.value
        .map(tab => tab.path)
        .filter(path => !keepPaths.has(path));
    invalidateTabCaches(closedPaths);
    tabs.value = currentTab && currentTab.path !== '/home'
        ? [homeTab, currentTab]
        : [homeTab];
};

// 点击标签页
const handleClick = (tab: any) => {
    const targetPath = tab.props.name;
    // 如果包含 query 参数，需要解析并传递
    if (targetPath.includes('?')) {
        const [path, queryString] = targetPath.split('?');
        const query: Record<string, string> = {};
        queryString.split('&').forEach(param => {
            const [key, value] = param.split('=');
            query[key] = decodeURIComponent(value);
        });
        router.push({ path, query });
    } else {
        router.push(targetPath);
    }
};

// 监听路由变化
watch(route, (newRoute) => {
    addTab(newRoute);

    // 重置面包屑
    BreadcrumbTitle.value = '';
    Breadcrumb.value = '';

    // 特殊处理首页
    if (newRoute.path === '/home') {
        BreadcrumbTitle.value = '首页';
        Breadcrumb.value = '首页';
        return;
    }

    // 从Pinia store中的菜单数据查找面包屑
    const findBreadcrumb = (menus: MenuItem[], targetPath: string): { title: string, childTitle: string } | null => {
        for (const item of menus) {
            // 如果是一级菜单
            if (item.path === targetPath) {
                return { title: item.meta?.title || item.name || '', childTitle: '' };
            }

            // 如果是子菜单
            if (item.children) {
                const child = item.children.find(child => child.path === targetPath);
                if (child) {
                    return { 
                        title: item.meta?.title || item.name || '', 
                        childTitle: child.meta?.title || child.name || '' 
                    };
                }
                
                // 递归查找更深层次的子菜单
                for (const childItem of item.children) {
                    if (childItem.children) {
                        const result = findBreadcrumb([childItem], targetPath);
                        if (result) {
                            return { 
                                title: item.meta?.title || item.name || '', 
                                childTitle: result.title 
                            };
                        }
                    }
                }
            }
        }
        return null;
    };

    const breadcrumb = findBreadcrumb(menuStore.sidebarMenus, newRoute.path);
    if (breadcrumb) {
        BreadcrumbTitle.value = breadcrumb.title;
        Breadcrumb.value = breadcrumb.childTitle || breadcrumb.title;
    }
}, { immediate: true });
</script>

<style lang="less" scoped>
// 定义CSS变量
:root {
    --sidebar-width: 200px;
    --sidebar-collapsed-width: 64px;
    --header-height: 60px;
    --tabs-height: 56px;
}

.layout {
    height: 100vh;
    position: relative;
    overflow: hidden;
    background-color: rgb(247, 248, 250);

    .main-container {
        height: 100%;
        display: flex;

        .side-menu-container {
            position: fixed;
            left: 0;
            top: 0;
            height: 100%;
            width: var(--sidebar-width);
            background-color: #001529;
            transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 1000;
            overflow: hidden;
            display: flex;
            flex-direction: column;

            &.collapsed {
                width: var(--sidebar-collapsed-width);
            }

            .header-section {
                color: #ffffff;
                padding: 12px 8px 8px 8px;
                min-height: 70px;
                flex-shrink: 0;
                display: flex;
                flex-direction: column;
                gap: 8px;
                transition: all 0.3s ease;

                .collapse-btn {
                    align-self: flex-end;
                    color: white;
                    cursor: pointer;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.3s ease;

                    &:hover {
                        background-color: #409eff;
                    }
                }

                .title-text {
                    font-size: 16px;
                    font-weight: bold;
                    text-align: center;
                    padding: 0 8px;
                    line-height: 1.4;
                    word-break: break-all;
                }
            }
        }

        .content-container {
            flex: 1;
            margin-left: var(--sidebar-width);
            transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
            height: 100vh;
            overflow: hidden;

            &.expanded {
                margin-left: var(--sidebar-collapsed-width);
            }

            .header {
                height: var(--header-height);
                background-color: white;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 16px;
                padding: 0 20px;
                border-bottom: 1px solid #dcdfe6;
                flex-shrink: 0;
                min-width: 0;

                .breadcrumb {
                    display: flex;
                    align-items: center;
                    min-width: 0;
                    flex: 1;
                    overflow: hidden;

                    span:nth-of-type(1) {
                        font-size: 14px;
                        font-weight: bold;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    span:nth-of-type(2) {
                        color: #c2c5d4;
                        font-size: 12px;
                        margin: 0 8px;
                    }

                    span:nth-of-type(3) {
                        font-size: 14px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .user-section {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    color: #303133;
                    flex-shrink: 0;
                    min-width: 0;
                    max-width: 220px;
                    white-space: nowrap;

                    .user-info {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        min-width: 0;
                        max-width: 180px;
                        font-size: 14px;
                        color: #606266;
                        overflow: hidden;
                        flex-shrink: 1;

                        span {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                    }

                    .logout-icon {
                        cursor: pointer;
                        color: #606266;
                        font-size: 18px;
                        transition: color 0.2s;

                        &:hover {
                            color: #409eff;
                        }
                    }
                }
            }

            .tabs-container {
                background-color: white;
                padding: 8px 16px;
                border-bottom: 1px solid #e4e7ed;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
                flex-shrink: 0;

                .tabs-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }

                .el-tabs {
                    flex: 1;
                    min-width: 0;
                }

                .tabs-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }

                :deep(.el-tabs__header) {
                    margin: 0;
                    border: none;
                }

                :deep(.el-tabs__nav-wrap::after) {
                    display: none;
                }

                :deep(.el-tabs__item) {
                    height: 36px;
                    line-height: 36px;
                    border: none;
                    border-radius: 4px;
                    margin-right: 8px;
                    padding: 0 16px;
                    font-size: 14px;
                    transition: all 0.3s;
                    position: relative;
                    border: 1px solid #dcdfe6;

                    &:hover {
                        color: #409eff;
                    }

                    &.is-active {
                        background-color: #409eff;
                        color: white;
                        font-weight: 500;
                    }

                    .el-icon-close {
                        border-radius: 50%;
                        text-align: center;
                        transition: all 0.3s;
                        transform-origin: 100% 50%;
                        margin-left: 8px;
                        font-size: 12px;
                        height: 16px;
                        width: 16px;
                        line-height: 16px;
                        vertical-align: middle;
                        top: -1px;
                        right: 2px;
                        color: #909399;

                        &:hover {
                            background-color: #c0c4cc;
                            color: white;
                        }
                    }

                    &.is-active .el-icon-close {
                        color: white;

                        &:hover {
                            background-color: rgba(255, 255, 255, 0.3);
                        }
                    }
                }

                :deep(.el-tabs__item[aria-controls="pane-home"]) {
                    .el-icon-close {
                        display: none !important;
                    }
                }

                :deep(.el-tabs__nav) {
                    border: none;
                }

                :deep(.el-tabs__nav-scroll) {
                    padding: 10;
                }
            }

            // 内容区域容器：统一负责页面 padding / 背景 / 滚动
            :deep(.router-view-container) {
                flex: 1;
                min-height: 0;
                overflow: auto;
                padding: var(--app-page-padding);
                background-color: var(--app-page-bg);
                box-sizing: border-box;
            }
        }
    }
}

// 菜单样式
.side-menu {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    background-color: #0f2438;
    border-right: none;
    scroll-behavior: smooth;
    padding: 8px 0;
    
    // 自定义滚动条样式
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
        transition: background 0.3s ease;
        
        &:hover {
            background: rgba(255, 255, 255, 0.5);
        }
    }
    
    &::-webkit-scrollbar-thumb:active {
        background: rgba(255, 255, 255, 0.6);
    }
    
    // 滚动条在折叠状态下的样式
    &.el-menu--collapse {
        &::-webkit-scrollbar {
            width: 4px;
        }
    }
}

.side-menu:not(.el-menu--collapse) {
    width: var(--sidebar-width);
}

:deep(.el-sub-menu__title) {
    > span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .iconfont {
        font-size: 14px;
        margin-right: 10px;
        transition: margin-right 0.3s ease;
    }
}

:deep(.el-menu-item) {
    background-color: #0f2438 !important;
    margin: 2px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;

    &.is-active {
        color: white !important;
    }

    &:hover {
        background-color: rgba(64, 158, 255, 0.1) !important;
        color: #409eff !important;
    }
}

:deep(.el-sub-menu) {
    .el-sub-menu__title {
        background-color: #0f2438 !important;
        margin: 2px 8px;
        border-radius: 4px;
        transition: all 0.3s ease;
        position: relative;
        
        &:hover {
            background-color: rgba(64, 158, 255, 0.1) !important;
            color: #409eff !important;
        }
        
        // 添加左侧指示线
        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 3px;
            height: 0;
            background: #409eff;
            border-radius: 0 2px 2px 0;
            transition: height 0.3s ease;
        }
        
        &:hover::before {
            height: 20px;
        }
    }
    
    .el-menu {
        background-color: #0f2438 !important;
        padding-left: 8px;
        
        .el-menu-item {
            background-color: #0f2438 !important;
            margin: 1px 16px;
            padding-left: 20px !important;
            border-radius: 4px;
            transition: all 0.3s ease;
            position: relative;
            
            &.is-active {
                color: white !important;
                
                &::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 3px;
                    height: 20px;
                    background: #409eff;
                    border-radius: 0 2px 2px 0;
                }
            }

            &:hover {
                background-color: rgba(64, 158, 255, 0.1) !important;
                color: #409eff !important;
            }
        }
    }
}

.menu-anchor {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    color: inherit;
    text-decoration: none;
    outline: none;

    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    span {
        margin-left: 5px;
    }
}

// 折叠侧栏时只隐藏侧栏中的一级叶子菜单文字；二级菜单悬浮层由
// Element Plus 传送到 body 下，不应继承折叠状态，否则弹层会显示为空白。
:deep(.side-menu.el-menu--collapse > .el-menu-item > .menu-anchor > .menu-label) {
    display: inline-block;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    width: 0;
}
</style>
